import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, Checkbox, SelectTag } from '../../../../../components';
import { Button, Container, Dialog, IconButton, Paper, Stack, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { ACCOUNT_GROUP_TYPE, INV_BALANCE_TYPE, YES_NO } from '../../../../../constants';

const TABLE_COLUMNS = [
    {
        header: 'Group Name',
        accessor: (row) => {
            const status = row.status;
            return (
                <span
                    style={{
                        color: status === 'inactive' ? 'red' : 'black',
                    }}
                >
                    {`${row.groupname}`}
                </span>
            );
        },
    },
    {
        header: 'Type',
        accessor: (row) => row?.group_type?.groupname
    },
    {
        header: 'Status',
        accessor: 'status',
        sx: { textTransform: 'uppercase' }
    },
    {
        header: 'Created At',
        accessor: 'createdAt'
    }
];

const initialGroupMasterDataObj = {
    data: [],
    loading: false
};

export const AccountGroups = () => {
    const [openQueryDialog, setOpenQueryDialog] = useState(false);
    const [groupMasterDataObj, setGroupMasterDataObj] = useState(initialGroupMasterDataObj);
    const [loading, setLoading] = useState(false);
    const [currentAction, setCurrentAction] = useState();
    const [selectedRecord, setSelectedRecord] = useState();
    const { pathname } = useLocation();
    const lastPathSegment = pathname.split('/').pop();
    const [grouptypeDropdown, setGrouptypeDropdown] = useState([])

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                groupname: Yup.string().required('Group Name  is required')
            })
        ),
        defaultValues: { groupname: '' },
        mode: 'all'
    });
    const { handleSubmit, setValue, reset } = methods;

    useEffect(() => {
        if (selectedRecord) {
            setValue('groupname', selectedRecord.groupname);
            setValue('group_type', selectedRecord.group_type?._id);
            setValue('drcr', selectedRecord.drcr);
            setValue('bspl', selectedRecord.bspl);

        } else {
            reset();
        }
    }, [setValue, reset, selectedRecord]);


    const getGroupTypeData = useCallback(async () => {
        const data = await dropdownFetch('account-group');
        setGrouptypeDropdown(data);
    }, []);

    useEffect(() => {
        getGroupTypeData()
    }, [getGroupTypeData])

    const formSubmit = async (formValues) => {
        setLoading(true);
        let editFlag = false;
        if (selectedRecord && currentAction === 'edit') {
            editFlag = true;
        }
        const response = await request(editFlag ? '/account-groups-update' : '/account-groups-create', {
            method: editFlag ? 'PUT' : 'POST',
            params: editFlag ? selectedRecord._id : null,
            body: {
                groupname: formValues.groupname,
                group_type: formValues.group_type,
                drcr: formValues.drcr,
                bspl: formValues.bspl,
                status: formValues.default ? 'default' : 'active'
            }
        });
        if (response?.success) {
            toast(`Successfully ${editFlag ? 'edited' : 'created'} Group master`, { variant: 'success' });
            setSelectedRecord(response.data);
            setCurrentAction();
        } else {
            toast(`Failed to ${editFlag ? 'edit' : 'create'} Group master`, { variant: 'error' });
        }
        setLoading(false);
    };

    const handleCancelRecord = async () => {
        setLoading(true);
        const response = await request('/account-groups-cancel', {
            method: 'DELETE',
            params: selectedRecord._id
        });
        if (response?.success) {
            toast('Successfully Cancelled Group master', { variant: 'success' });
            setSelectedRecord();
        } else {
            toast('Failed to Cancelled Group Master', { variant: 'error' });
        }
        setLoading(false);
    };


    const handleDeleteRecord = async () => {
        setLoading(true);
        const response = await request('/account-groups-delete', {
            method: 'DELETE',
            params: selectedRecord._id
        });
        if (response?.success) {
            toast('Successfully deleted Group master', { variant: 'success' });
            setSelectedRecord();
        } else {
            toast('Failed to delete Group Master', { variant: 'error' });
        }
        setLoading(false);
    };

    const handleQueryWindow = async () => {
        setOpenQueryDialog(true);
        setGroupMasterDataObj({ loading: true });
        const response = await request('/account-groups-list', { method: 'GET' });
        if (response.success) {
            const filteredData = response.data.filter(item =>
                item.status === 'active' || item.status === 'inactive' || item.status === 'default'
            );
            setGroupMasterDataObj({ loading: false, data: filteredData });
        } else {
            toast('Error in fetching data', { variant: 'error' });
            setGroupMasterDataObj({ loading: false });
        }
    };

    return (
        <>
            <Paper sx={{
                p: 0, flex: 1, borderRadius: 0, boxShadow: 'none',
                width: '100%',
                height: 'auto'
            }}>
                <Container
                    sx={{
                        mt: 1,
                        border: '0px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        width: '100%',
                        maxWidth: 'none',
                        minWidth: "100%",
                        boxSizing: 'border-box',

                    }}
                >
                    <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                mb: 1,
                                borderBottom: '0px solid',
                                borderColor: 'grey.300'
                            }}
                        >
                            <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                                Account Group
                            </Typography>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                                <CustomButtonn
                                    id={`${lastPathSegment}Write`}
                                    variant="contained"
                                    disabled={['edit', 'new'].includes(currentAction)}
                                    onClick={() => {
                                        setSelectedRecord();
                                        setCurrentAction('new');
                                        reset();
                                    }}
                                >
                                    New
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`${lastPathSegment}Update`}
                                    variant="contained"
                                    disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                                    onClick={() => setCurrentAction('edit')}
                                >
                                    Edit
                                </CustomButtonn>
                                <CustomButtonn
                                    id={"Submit"}
                                    type="submit"
                                    variant="contained"
                                    disabled={!['edit', 'new'].includes(currentAction) || loading}
                                >
                                    Save
                                </CustomButtonn>
                                <CustomButtonn
                                    id={"Cancel"}
                                    variant="contained"
                                    disabled={['edit', 'new'].includes(currentAction) || loading}
                                    onClick={() => {
                                        setCurrentAction();
                                        handleCancelRecord()
                                    }}
                                >
                                    Cancel
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`isSuperAdmin`}
                                    variant="contained"
                                    disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                                    onClick={handleDeleteRecord}
                                >
                                    Delete
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`${lastPathSegment}Read`}
                                    variant="contained"
                                    onClick={handleQueryWindow}
                                    disabled={['new', 'edit'].includes(currentAction)}
                                >
                                    Query
                                </CustomButtonn>
                                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained" disabled={!selectedRecord || currentAction}>
                                    Print
                                </CustomButtonn>
                                <CustomButtonn id={"Exit"} variant="contained"
                                    onClick={() => {
                                        setCurrentAction('');
                                    }}
                                >Exit</CustomButtonn>
                            </Stack>
                        </Stack>
                        <Stack spacing={2}> {/* Add spacing between the Stack components */}
                            <Box sx={{ border: '0px solid gray', padding: 2, borderRadius: '8px', width: '60%', boxShadow: '0px 2px 10px #0099ff' }}>

                                {/* Group Name Stack with marginBottom */}
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ marginBottom: '2px' }}>
                                    <Typography sx={{ flex: 0.15, fontSize: '1.4rem', fontWeight: 700 }}>Group Name</Typography>
                                    <TextField
                                        name="groupname"
                                        sx={{ flex: 0.8, border: currentAction ? "0.5px solid red" : "" }}
                                        inputProps={{ sx: { fontSize: '1.4rem' } }}
                                        placeholder="Enter Group Name"
                                        fullWidth
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                    />
                                </Stack>

                                {/* Group Type Stack with marginBottom */}
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ marginBottom: '2px' }}>
                                    <Typography sx={{ flex: 0.15, fontSize: '1.4rem', fontWeight: 700 }}> Group Type</Typography>
                                    <SelectTag
                                        name="group_type"
                                        formControlSX={{ flex: 0.8, }}
                                        placeholder="Enter Group Type"
                                        fullWidth
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                        menus={grouptypeDropdown.map((data) => ({ ...data, name: data.groupname }))}
                                    />
                                </Stack>

                                {/* BSPL and DrCr Stack */}
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography sx={{ flex: 0.16, fontSize: '1.4rem', fontWeight: 700 }}>BSPL</Typography>
                                    <SelectTag
                                        name="bspl"
                                        formControlSX={{ flex: 0.4 }}
                                        placeholder="BSPL"
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                        menus={ACCOUNT_GROUP_TYPE}
                                    />

                                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>DrCr</Typography>
                                    <SelectTag
                                        name="drcr"
                                        formControlSX={{ flex: 0.39 }}
                                        placeholder="DRCR"
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                        menus={INV_BALANCE_TYPE}
                                    />
                                </Stack>

                            </Box>
                        </Stack>

                    </FormProvider>
                </Container>
            </Paper >
            <Dialog
                open={openQueryDialog}
                onClose={() => setOpenQueryDialog(false)}
                PaperProps={{ sx: { width: '80%', p: 2 } }}
                maxWidth="xl"
                scroll="body"
            >
                <Stack
                    padding={0}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    borderBottom="1px solid"
                    borderColor="grey.300"
                >
                    <Typography variant="h4" fontWeight="bold">
                        Account Group Master Query Window
                    </Typography>
                    <IconButton onClick={() => setOpenQueryDialog(false)}>
                        <CloseIcon color="error" fontSize="large" />
                    </IconButton>
                </Stack>
                <Table
                    sx={{ mt: 0.5 }}
                    columns={TABLE_COLUMNS}
                    data={groupMasterDataObj.data || []}
                    loading={groupMasterDataObj.loading}
                    onRowClick={(row) => {
                        setOpenQueryDialog(false);
                        setSelectedRecord(row);
                    }}
                />
            </Dialog>
        </>
    );
};
